// ============================================
// RISK FLAGS COMPONENT (V3 COMPATIBLE)
// Displays Earnings, FOMC, Liquidity & Warnings
// ============================================

import React from 'react';
import { RiskFlags as RiskFlagsType } from '../types';
import { AlertTriangle, ShieldCheck, Zap, Calendar, TrendingUp, Droplets, Activity } from 'lucide-react';

interface RiskFlagsProps {
  flags: RiskFlagsType;
}

export const RiskFlags: React.FC<RiskFlagsProps> = ({ flags }) => {
  const {
    earningsInDays,
    fomcInDays,
    liquidityPass,
    liquidityGrade,
    ivRank,
    ivStrategy,
    warningFlags,
    criticalFlags,
    autoReject,
    vixLevel
  } = flags;

  return (
    <div className="bg-terminal-card rounded-xl border border-terminal-border overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-terminal-border bg-terminal-bg/50 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-blue-400" size={18} />
          <h3 className="font-bold text-terminal-bright">Risk Assessment v3.0</h3>
        </div>
        
        {autoReject && (
          <span className="px-3 py-1 bg-rose-500/10 border border-rose-500/30 rounded text-rose-400 text-xs font-bold uppercase">
            ⛔ Auto-Rejected
          </span>
        )}
      </div>

      <div className="p-6 flex-1 flex flex-col gap-6">
        
        {/* TOP SECTION: Critical Alerts */}
        {(criticalFlags.length > 0 || warningFlags.length > 0) ? (
          <div className="space-y-3">
            {criticalFlags.map((flag, i) => (
              <div key={`crit-${i}`} className="flex items-center gap-3 p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-400 text-sm font-bold">
                <AlertTriangle size={16} />
                {flag}
              </div>
            ))}
            {warningFlags.map((flag, i) => (
              <div key={`warn-${i}`} className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
                <AlertTriangle size={16} />
                {flag}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg flex items-center gap-3 text-emerald-400 text-sm">
            <ShieldCheck size={18} />
            <span className="font-bold">System Clear: No elevated risks detected.</span>
          </div>
        )}

        {/* MIDDLE SECTION: Risk Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">

          {/* VIX Level - Prominent */}
          <div className={`bg-slate-900/50 p-3 rounded-lg border ${
            vixLevel && vixLevel >= 30 ? 'border-rose-500/50 bg-rose-500/10' :
            vixLevel && vixLevel >= 25 ? 'border-amber-500/50 bg-amber-500/10' :
            'border-slate-800'
          }`}>
            <div className="text-sm text-slate-300 uppercase font-bold mb-1 flex items-center gap-2">
              <Activity size={12} /> VIX Level
            </div>
            <div className={`text-lg font-bold ${
              vixLevel && vixLevel >= 30 ? 'text-rose-400' :
              vixLevel && vixLevel >= 25 ? 'text-amber-400' :
              vixLevel && vixLevel >= 20 ? 'text-yellow-400' :
              'text-emerald-400'
            }`}>
              {vixLevel ? vixLevel.toFixed(1) : 'N/A'}
            </div>
            <div className="text-sm mt-1 text-slate-300">
              {vixLevel && vixLevel >= 30 ? 'EXTREME' :
               vixLevel && vixLevel >= 25 ? 'HIGH' :
               vixLevel && vixLevel >= 20 ? 'ELEVATED' :
               vixLevel && vixLevel >= 15 ? 'NORMAL' : 'LOW'}
            </div>
          </div>

          {/* Earnings + IV Crush Warning */}
          <div className={`bg-slate-900/50 p-3 rounded-lg border ${
            earningsInDays !== null && earningsInDays < 7 ? 'border-rose-500/50 bg-rose-500/10' :
            earningsInDays !== null && earningsInDays < 14 ? 'border-amber-500/50 bg-amber-500/10' :
            'border-slate-800'
          }`}>
            <div className="text-sm text-slate-300 uppercase font-bold mb-1 flex items-center gap-2">
              <Calendar size={12} /> Earnings
            </div>
            <div className={`text-lg font-bold ${
              earningsInDays !== null && earningsInDays < 14 ? 'text-rose-400' :
              earningsInDays !== null && earningsInDays < 30 ? 'text-amber-400' : 'text-white'
            }`}>
              {earningsInDays !== null ? `${earningsInDays} Days` : 'N/A'}
            </div>
            {earningsInDays !== null && earningsInDays <= 14 && (
              <div className="text-xs text-rose-400 font-bold mt-1">
                IV Crush risk — premiums inflated
              </div>
            )}
          </div>

          {/* FOMC - Red border + bg when < 7 days */}
          <div className={`bg-slate-900/50 p-3 rounded-lg border ${
            fomcInDays !== null && fomcInDays < 7 ? 'border-rose-500/50 bg-rose-500/10' :
            fomcInDays !== null && fomcInDays < 14 ? 'border-amber-500/50 bg-amber-500/10' :
            'border-slate-800'
          }`}>
            <div className="text-sm text-slate-300 uppercase font-bold mb-1 flex items-center gap-2">
              <Activity size={12} /> FOMC Meeting
            </div>
            <div className={`text-lg font-bold ${
              fomcInDays !== null && fomcInDays < 7 ? 'text-rose-400' :
              fomcInDays !== null && fomcInDays < 14 ? 'text-amber-400' : 'text-white'
            }`}>
              {fomcInDays !== null ? `${fomcInDays} Days` : 'None Near'}
            </div>
            {fomcInDays !== null && fomcInDays < 7 && (
              <div className="text-xs text-rose-400 font-bold mt-1">Rate decision imminent</div>
            )}
          </div>

          {/* Liquidity */}
          <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-800">
            <div className="text-sm text-slate-300 uppercase font-bold mb-1 flex items-center gap-2">
              <Droplets size={12} /> Liquidity
            </div>
            <div className={`text-lg font-bold ${liquidityPass ? 'text-emerald-400' : 'text-rose-400'}`}>
              Grade {liquidityGrade}
            </div>
          </div>

          {/* IV Rank */}
          <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-800">
            <div className="text-sm text-slate-300 uppercase font-bold mb-1 flex items-center gap-2">
              <TrendingUp size={12} /> IV Rank
            </div>
            <div className={`text-lg font-bold ${ivRank > 50 ? 'text-cyan-400' : 'text-slate-300'}`}>
              {ivRank.toFixed(0)}
            </div>
          </div>

        </div>

        {/* BOTTOM SECTION: Strategy Context */}
        <div className="mt-auto bg-slate-950 p-4 rounded-lg border border-slate-800">
          <div className="flex items-center gap-2 mb-2 text-xs font-bold text-slate-300 uppercase">
            <Zap size={12} className="text-yellow-400" /> Market Context
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-300">Recommended Approach:</span>
            <span className={`font-bold px-2 py-0.5 rounded text-xs border ${
              ivStrategy === 'SELL_PREMIUM' 
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
                : 'bg-blue-500/10 border-blue-500/30 text-blue-400'
            }`}>
              {ivStrategy === 'SELL_PREMIUM' ? 'CREDIT' : 'DEBIT'} STRATEGY
            </span>
          </div>
          <div className="text-sm text-slate-300 mt-2">
            {ivStrategy === 'SELL_PREMIUM' 
              ? 'High IV detects premium selling opportunity.' 
              : 'Low IV favors buying spreads (debit).'}
          </div>
        </div>

      </div>
    </div>
  );
};
